import React, { useCallback } from 'react';

import { Icon as IconType, IconProps } from '@rover/icons/utils';
import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import { isProdEnv } from '@rover/rsdk/src/modules/Env/node';
import { Logger } from '@rover/rsdk/src/modules/Logging';

import {
  getIsTextSized,
  IconSizeMap,
  LinkColorMap,
  LinkCommonProps,
  LinkHoverColorMap,
  TypographyComponentProps,
} from './Link.common';

export type Props = LinkCommonProps & {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  as?: 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  href: string; // href is required on web
  rel?: 'noopener' | 'noreferrer' | 'nofollow' | 'noopener noreferrer';
  sx?: React.ComponentProps<typeof Flex>['sx'];
  target?: '_blank' | '_self' | '_parent' | '_top' | string; // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#target
  /* This handler is intended for very fast actions before navigating to href, like sending analytics. Do not await anything slow in this handler! */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

type ExtendedIconType = IconProps & {
  flexShrink?: string;
  marginLeft?: string;
  marginRight?: string;
};

export const LinkIcon: React.FC<ExtendedIconType & { icon: IconType }> = ({
  icon: Icon,
  // the destructuring of these props prevents errors from them being passed to an <svg />
  flexShrink, // eslint-disable-line @typescript-eslint/no-unused-vars
  marginLeft, // eslint-disable-line @typescript-eslint/no-unused-vars
  marginRight, // eslint-disable-line @typescript-eslint/no-unused-vars
  ...other
}) => {
  return <Icon {...other} fill="currentColor" />;
};

const getTypographyComponentProps = ({
  fontWeight,
  underline,
  asInlineLink,
  variant,
}: {
  fontWeight: Props['fontWeight'];
  underline: Props['underline'];
  asInlineLink?: true;
  variant: NonNullable<Props['variant']>;
}): TypographyComponentProps<'sx'> => {
  return {
    // this spread+ternary avoids passing fontWeight: undefined which breaks default fontWeights on the typog components
    ...(fontWeight ? { fontWeight } : undefined),
    textDecoration: underline ? 'underline' : 'none',
    sx: {
      display: asInlineLink ? 'inline' : 'inline-flex',
      alignItems: 'center',
      color: LinkColorMap[variant],
      '&:hover': {
        color: LinkHoverColorMap[variant],
        textDecoration: 'underline',
      },
    },
  };
};

function Link({
  as,
  children,
  icon,
  iconAlignment = 'left',
  asInlineLink,
  size = '200',
  sx,
  testID,
  underline = true,
  variant = 'primary',
  fontWeight,
  target = '_self',
  href,
  onClick,
  ...other
}: Props): JSX.Element {
  if (!children && !other['aria-label'] && !other['aria-labelledby']) {
    const error = new Error('Link.props MUST have children, or a11y values');
    if (isProdEnv()) {
      Logger.error(error);
    } else {
      console.error(error.stack);
      throw error;
    }
  }

  const handleClick = useCallback(
    async (e) => {
      // if no onClick handler or we are not on the client, allow normal href behavior
      if (!onClick || (!process.env.JS_ENV_CLIENT && process.env.NODE_ENV !== 'test')) return;
      // prevent default behavior of navigating to href
      e.preventDefault();
      // await the onClick handler before navigating to href
      try {
        await onClick(e);
      } finally {
        if (href !== '') window.open(href, target);
      }
    },
    [onClick, href, target]
  );

  const styledIcon = icon && (
    <Box
      as={LinkIcon}
      flexShrink="0"
      height={IconSizeMap[size]}
      icon={icon}
      width={IconSizeMap[size]}
      {...(iconAlignment === 'left' ? ({ mr: '2x' } as const) : ({ ml: '2x' } as const))}
    />
  );

  const content = (
    <>
      {iconAlignment === 'left' && styledIcon}
      {children}
      {iconAlignment === 'right' && styledIcon}
    </>
  );

  const typog = getIsTextSized(size) ? (
    <Text
      {...getTypographyComponentProps({ fontWeight, variant, underline, asInlineLink })}
      size={size}
      // @ts-expect-error some of the heading tags are not normally allowed on Text.  User is responsible for creating sensical html ;p
      as={as || 'span'}
      data-testid="kc-link-text"
      display={asInlineLink ? 'inline' : undefined}
    >
      {content}
    </Text>
  ) : (
    <Heading
      {...getTypographyComponentProps({ fontWeight, variant, underline, asInlineLink })}
      size={size}
      as={as || 'div'}
      data-testid="kc-link-text"
    >
      {content}
    </Heading>
  );

  return (
    <Flex
      as="a"
      data-testid={testID}
      display={asInlineLink ? 'inline' : 'inline-flex'}
      target={target}
      sx={{ textDecoration: 'none !important', verticalAlign: 'bottom', ...sx }}
      href={href}
      onClick={handleClick}
      {...other}
    >
      {typog}
    </Flex>
  );
}

export default Link;
